import { UserBasedReturnMapIdType } from "@shared/constants/enums/enums-types";
import Repository, {
  ExtendedAxiosRequestConfig,
} from "@shared/services/api/abstracts/Repository";
import { UrlRouteMap } from "@shared/services/api/types";
import { taskLoader } from "@shared/services/loaders/task-loader";
import { AxiosRequestConfig } from "axios";
import { UserBasedInvoiceShippingAddressSerializer } from "@shared/modules/financials/types/invoice-items";
import {
  ExchangeTicketsAmountsType,
  FreeTicketsToReturnResponseSerializer,
  ReturnOrderAmountsType,
  UserInvoiceDetailSerializer,
} from "../types/invoice";

export default class UserBasedInvoiceRepository extends Repository<UserInvoiceDetailSerializer> {
  public readonly repositoryName = "UserBasedInvoiceRepository";

  public readonly endpoint = "/api/user/financials/invoices/";

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      invoices: "v2/",
      getFreeTicketsToReturn: `{transactionId}/get-return-tickets/`,
      freeReturn: `{transactionId}/free-return`,
      getExchangeAmounts: `{invoiceId}/get-exchange-amounts/`,
      transaction: `{transactionId}/transaction/`,
      updateShippingAddress: `{transactionId}/shipping-address/`,
      getReturnAmounts: `{invoiceId}/get-return-amounts/`,
      returnInvoice: `{invoiceId}/return/`,
      exchangeInvoice: `{invoiceId}/exchange-tickets/`,
      resendConfirmationEmail: `{transactionId}/notify/`,
      itemizedExchange: `{invoiceId}/itemized-exchange/`,
    };
  }

  public async getFreeTicketsToReturn(
    transactionId: string,
    config: AxiosRequestConfig = {}
  ): Promise<FreeTicketsToReturnResponseSerializer> {
    const { data } = await this.request<FreeTicketsToReturnResponseSerializer>(
      this.buildUrl("getFreeTicketsToReturn", { transactionId }),
      {
        method: "get",
        ...config,
      }
    );

    return data;
  }

  public async freeReturn(
    transactionId: string,
    freeReturnData: {
      tickets: Array<number>;
      return_reason?: UserBasedReturnMapIdType;
    },
    config: AxiosRequestConfig = {}
  ) {
    const { data } = await this.request<{ job_ids: string[] }>(
      this.buildUrl("freeReturn", { transactionId }),
      {
        method: "post",
        data: freeReturnData,
        ...config,
      }
    );

    // Process each job_id using taskLoader and wait for all to complete.
    // If any of the jobs fail, reject and prevent further `taskLoader` tasks from loading.
    const results = await Promise.all(
      data.job_ids.map((jobId) => taskLoader(jobId))
    );

    return results[0];
  }

  public async getExchangeAmounts(
    invoiceId: number,
    config: ExtendedAxiosRequestConfig = {}
  ): Promise<ExchangeTicketsAmountsType> {
    const { data } = await this.request<ExchangeTicketsAmountsType>(
      this.buildUrl("getExchangeAmounts", { invoiceId }),
      {
        method: "get",
        ...config,
      }
    );

    return data;
  }

  public async transaction(
    transactionId: string,
    config: ExtendedAxiosRequestConfig = {}
  ): Promise<UserInvoiceDetailSerializer> {
    const { data } = await this.request(
      this.buildUrl("transaction", { transactionId }),
      {
        method: "get",
        ...config,
      }
    );

    return data;
  }

  public async updateShippingAddress(
    transactionId: string,
    shippingAddressData: {
      street_name: string;
      unit_number?: string;
      city: string;
      province: string;
      country: string;
      postal: string;
      addressee: string;
    },
    config: AxiosRequestConfig = {}
  ): Promise<UserBasedInvoiceShippingAddressSerializer> {
    const { data } =
      await this.request<UserBasedInvoiceShippingAddressSerializer>(
        this.buildUrl("updateShippingAddress", { transactionId }),
        {
          method: "post",
          data: shippingAddressData,
          ...config,
        }
      );

    return data;
  }

  public async getReturnAmounts(
    {
      invoiceId,
      tickets,
      is_resale,
    }: {
      invoiceId: number;
      tickets: number[];
      is_resale?: boolean;
    },
    config: AxiosRequestConfig = {}
  ) {
    const { data } = await this.request<ReturnOrderAmountsType>(
      this.buildUrl("getReturnAmounts", { invoiceId }),
      {
        method: "get",
        params: {
          tickets: tickets.toString(),
          is_resale,
        },
        ...config,
      }
    );

    return data;
  }

  public async returnInvoice(
    {
      invoiceId,
      tickets,
    }: {
      invoiceId: number;
      tickets: number[];
    },
    config: AxiosRequestConfig = {}
  ) {
    const { data } = await this.request(
      this.buildUrl("returnInvoice", { invoiceId }),
      {
        method: "post",
        data: {
          tickets,
        },
        ...config,
      }
    );

    return data;
  }

  public async exchangeInvoice(
    invoiceId: number,
    config: AxiosRequestConfig = {}
  ) {
    const { data } = await this.request(
      this.buildUrl("exchangeInvoice", { invoiceId }),
      {
        method: "post",
        ...config,
      }
    );

    return data;
  }

  public async resendConfirmationEmail(
    {
      transactionId,
      email,
      language,
    }: {
      transactionId: string;
      email: string;
      language?: "en" | "fr";
    },
    config: AxiosRequestConfig = {}
  ) {
    const { data } = await this.request<{ email: string }>(
      this.buildUrl("resendConfirmationEmail", { transactionId }),
      {
        method: "post",
        data: {
          id: transactionId,
          email,
          ...(language && { language }),
        },
        ...config,
      }
    );

    return data;
  }

  public async itemizedExchange(
    transactionId: string,
    config?: AxiosRequestConfig
  ): Promise<UserInvoiceDetailSerializer> {
    const { data } = await this.request<UserInvoiceDetailSerializer>(
      this.buildUrl("itemizedExchange", { transactionId }),
      {
        method: "post",
        ...config,
      }
    );

    return data;
  }
}
